import type * as THREE from "three";
import type { WebGLExtendedCapabilities } from "../../WebGLExtendedCapabilities";
import type { AOAlgorithm } from "./AOAlgorithm";
import type { DepthTextureType } from "./DepthTextureType";
import type { SSFXRendererOutput } from "./SSFXRendererOutput";
import type { RenderTargetPassAccess } from "./SSFXRendererParams";
import { defaultRenderPipelineParams } from "./SSFXRendererParams";
import type { RenderPipelineParams } from "./SSFXRendererTypes";

export class RenderPipelineParamsContainer implements RenderPipelineParams {
  private outputInternal: SSFXRendererOutput;
  private aoAlgorithmInternal: AOAlgorithm;
  private downsampleDepthStepsInternal: number;
  private depthTextureTypeInternal: DepthTextureType;

  constructor(
    private renderTarget: RenderTargetPassAccess,
    capabilities: WebGLExtendedCapabilities,
  ) {
    this.outputInternal = defaultRenderPipelineParams.output;
    this.aoAlgorithmInternal = defaultRenderPipelineParams.aoAlgorithm;
    this.downsampleDepthStepsInternal = defaultRenderPipelineParams.downsampleDepthSteps;
    this.depthTextureTypeInternal = capabilities.defaultDepthTextureType;
  }

  get output(): SSFXRendererOutput {
    return this.outputInternal;
  }

  set output(value: SSFXRendererOutput) {
    const intValue = typeof value === "string" ? parseInt(value) : value; // The type can be string we set from dat.gui.
    this.outputInternal = intValue as SSFXRendererOutput;
  }

  get aoAlgorithm(): AOAlgorithm {
    return this.aoAlgorithmInternal;
  }

  set aoAlgorithm(value: AOAlgorithm) {
    const intValue = typeof value === "string" ? parseInt(value) : value; // The type can be string we set from dat.gui.
    this.aoAlgorithmInternal = intValue;
  }

  get downsampleDepthSteps(): number {
    return this.downsampleDepthStepsInternal;
  }

  set downsampleDepthSteps(value: number) {
    this.downsampleDepthStepsInternal = value;
    this.renderTarget.depthNormalPass.params.downsampleDepthSteps = value;
  }

  get depthTextureType(): DepthTextureType {
    return this.depthTextureTypeInternal;
  }

  set depthTextureType(value: DepthTextureType) {
    const intValue = typeof value === "string" ? parseInt(value) : value; // The type can be string we set from dat.gui.
    if (intValue === this.depthTextureTypeInternal) {
      return;
    }
    this.depthTextureTypeInternal = intValue as DepthTextureType;
    this.renderTarget.depthNormalPass.depthTextureType = intValue as DepthTextureType;
    this.renderTarget.saoPass.depthTextureType = intValue as DepthTextureType;
    this.renderTarget.gtaoPass.depthTextureType = intValue as DepthTextureType;
  }

  // Other properties from RenderPipelineParams interface
  transparentBackground: boolean = defaultRenderPipelineParams.transparentBackground;
  useHalfFloatLinearRenderBuffer: boolean = defaultRenderPipelineParams.useHalfFloatLinearRenderBuffer;
  toneMapping: THREE.ToneMapping = defaultRenderPipelineParams.toneMapping;
  includeOverlayInOutput: boolean = defaultRenderPipelineParams.includeOverlayInOutput;
  reflectionsEnabled: boolean = defaultRenderPipelineParams.reflectionsEnabled;
  screenSpaceShadowsEnabled: boolean = defaultRenderPipelineParams.screenSpaceShadowsEnabled;
  smallScaleAOEnabled: boolean = defaultRenderPipelineParams.smallScaleAOEnabled;
  substitutedReflectionMultiplier: number = defaultRenderPipelineParams.substitutedReflectionMultiplier;
  isolateModeSupported: boolean = defaultRenderPipelineParams.isolateModeSupported;
}
